<template>
  <div>
    <el-dialog
      :width="crossProvider ? '1000px' : '632px'"
      :visible="showStepOne"
      :before-close="handleClose"
      :modal-append-to-body="false"
      v-loading="loading"
    >
      <div slot="title">
        <div class="el-dialog__title">
          {{ $t('CopyTemplateDialog_Title') }}
        </div>
        <div class="sub-title">
          {{ $t('CopyTemplateDialog_SelectProject') }}
        </div>
      </div>
      <div class="items-container">
        <div class="p-bottom-24">
          <el-input v-model="projectFilter">
            <i slot="prefix" class="el-input__icon el-icon-search" />
          </el-input>
        </div>
        <radio-group v-model="selectedProject">
          <el-table
            :data="filteredProjects"
            :stripe="true"
            class="table-container outlined"
            :max-height="500"
          >
            <el-table-column :label="$t('CopyTemplateDialog_Project')">
              <template slot-scope="scope">
                <radio :label="scope.row" :title="scope.row.name" />
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('CopyTemplateDialog_Provider')"
              v-if="crossProvider"
              prop="provider.name"
            />
            <el-table-column
              :label="$t('CopyTemplateDialog_Company')"
              v-if="crossProvider"
              prop="company.name"
            />
            <el-table-column
              :label="$t('CopyTemplateDialog_CreatedOn')"
              prop="create_time"
              :formatter="dateFormatter"
            />
          </el-table>
        </radio-group>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          :disabled="!selectedProject"
          @click="onNextBtnClick"
        >
          <span>{{ $t('CopyTemplateDialog_Button_Next') }}</span>
          <i class="el-icon-arrow-right el-icon--right" />
        </el-button>
      </span>
    </el-dialog>
    <el-dialog
      :width="'632px'"
      :visible.sync="showStepTwo"
      :before-close="handleClose"
      :modal-append-to-body="false"
    >
      <div slot="title">
        <div class="el-dialog__title">
          {{ $t('CopyTemplateDialog_Title') }}
        </div>
        <div class="sub-title">
          {{ $t('CopyTemplateDialog_CheckDependencies') }}
        </div>
      </div>
      <div v-loading="polling" class="dialog-body">
        <template v-if="errors.length">
          <div class="warning-msg">
            {{ $t('CopyTemplateDialog_ErrorMsg') }}
          </div>
          <div class="warning-items">
            <ul v-for="item in errors">
              <li>{{ $t(errorItems[item] || item) }}</li>
            </ul>
          </div>
        </template>
        <template v-else-if="warnings.length">
          <div class="warning-msg">
            {{ $t('CopyTemplateDialog_WarningMsg') }}
          </div>
          <div class="warning-items">
            <ul v-for="item in warnings">
              <li>{{ $t(warningItems[item] || item) }}</li>
            </ul>
          </div>
        </template>
        <div v-else class="warning-msg centered">
          {{ $t('CopyTemplateDialog_ConfirmMsg') }}
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          icon="el-icon-check"
          :disabled="!!Object.keys(errors).length || polling"
          @click="onCopyBtnClick"
        >
          {{ $t('CopyTemplateDialog_Button_Copy') }}
        </el-button>
        <el-button
          type="primary"
          plain
          icon="el-icon-arrow-left"
          @click="onBackBtnClick"
          :disabled="polling"
        >
          {{ $t('CopyTemplateDialog_Button_Back') }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import RadioGroup from '@/common/components/form/RadioGroup'
import Radio from '@/common/components/form/Radio'
import moment from 'moment'
import { RestApiPaginator } from '@/core/utils/RestApiPaginator'
import { projectEndpoint } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'

export default {
  name: 'CopyTemplateDialog',
  components: { Radio, RadioGroup },
  created() {
    let params = {
      sort: 'title',
      expand: 'create_time'
    }

    if (!this.crossProvider) {
      params.company_id = this.$store.state.app.project.company.id
    }

    new RestApiPaginator({
      method: 'get',
      url: `projects`,
      params
    })
      .fetchAllPages()
      .then(data => {
        this.availableProjects = data.filter(
          project => project.id !== parseInt(this.projectId)
        )
        this.loading = false
      })
      .catch(error => {
        this.$message({
          message: error.message,
          type: 'error'
        })
        this.loading = false
      })
  },
  props: {
    templateId: {
      type: Number,
      required: false
    },
    dialogVisible: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      showStepOne: false,
      showStepTwo: false,
      availableProjects: [],
      selectedProject: null,
      projectFilter: '',
      errors: {},
      warnings: {},
      loading: false,
      taskId: null,
      polling: false,
      errorItems: {
        pick_a_winner: 'CopyTemplateDialog_Error_PaW',
        push_encryption: 'CopyTemplateDialog_Error_PushEncryption',
        feature_seed_list: 'CopyTemplateDialog_Error_SeedList',
        optimove: 'CopyTemplateDialog_Error_OptimoveProject',
        salesforce: 'CopyTemplateDialog_Error_Salesforce',
        inapp_native: 'CopyTemplateDialog_Error_InappNative',
        email_attachments: 'CopyTemplateDialog_Error_EmailAttachments',
        campaign_templates: 'CopyTemplateDialog_Error_CampaignTemplates',
        workflow_email_attachments:
          'CopyTemplateDialog_Error_Workflow_EmailAttachments',
        workflow_push_encryption:
          'CopyTemplateDialog_Error_Workflow_PushEncryption',
        feature_campaign_throttling:
          'CopyTemplateDialog_Error_CampaignThrottling',
        channel_email: 'CopyTemplateDialog_Error_Channel_Email',
        channel_sms: 'CopyTemplateDialog_Error_Channel_Sms',
        channel_webhook: 'CopyTemplateDialog_Error_Channel_Webhook',
        channel_facebook: 'CopyTemplateDialog_Error_Channel_Facebook',
        channel_whatsapp: 'CopyTemplateDialog_Error_Channel_Whatsapp',
        channel_inapp: 'CopyTemplateDialog_Error_Channel_Inapp',
        channel_inapp_android: 'CopyTemplateDialog_Error_Channel_Inapp_Android',
        channel_inapp_ios: 'CopyTemplateDialog_Error_Channel_Inapp_Ios',
        channel_onsite: 'CopyTemplateDialog_Error_Channel_Onsite',
        channel_webpush: 'CopyTemplateDialog_Error_Channel_Webpush',
        channel_android_push: 'CopyTemplateDialog_Error_Channel_Android_Push',
        channel_ios_push: 'CopyTemplateDialog_Error_Channel_Ios_Push',
        channel_inbox: 'CopyTemplateDialog_Error_Channel_Inbox',
        workflow_missing_channel_android:
          'CopyTemplateDialog_Warning_Workflow_MissingChannel_Android',
        workflow_missing_channel_email:
          'CopyTemplateDialog_Warning_Workflow_MissingChannel_Email',
        workflow_missing_channel_ios:
          'CopyTemplateDialog_Warning_Workflow_MissingChannel_Ios',
        workflow_missing_channel_sms:
          'CopyTemplateDialog_Warning_Workflow_MissingChannel_Sms',
        workflow_missing_channel_web:
          'CopyTemplateDialog_Warning_Workflow_MissingChannel_Web',
        mxa: 'CopyTemplateDialog_Error_No_Mxa'
      },
      warningItems: {
        subscribe_to: 'CopyTemplateDialog_Warning_SubscriptionPreferences',
        add_to_list: 'CopyTemplateDialog_Warning_UserList',
        microsite: 'CopyTemplateDialog_Warning_MicrositeUrl',
        user_attribute: 'CopyTemplateDialog_Warning_UserAttribute',
        snippet: 'CopyTemplateDialog_Warning_Snippet',
        locations: 'CopyTemplateDialog_Warning_Locations',
        promotion: 'CopyTemplateDialog_Warning_Promotion',
        engagement_category: 'CopyTemplateDialog_Warning_EngagementCategory',
        push_category: 'CopyTemplateDialog_Warning_PushCategory',
        segment: 'CopyTemplateDialog_Warning_Segment',
        shared_segment: 'CopyTemplateDialog_Warning_SharedSegment',
        segment_locations: 'CopyTemplateDialog_Warning_SegmentationLocations',
        application: 'CopyTemplateDialog_Warning_Application',
        csv: 'CopyTemplateDialog_Warning_Csv',
        segment_list: 'CopyTemplateDialog_Warning_SegmentationUserList',
        tags_attribute: 'CopyTemplateDialog_Warning_Attribute',
        subscription_preference:
          'CopyTemplateDialog_Warning_SegmentationSubscriptionPreference',
        workflow_decision_segment:
          'CopyTemplateDialog_Warning_Workflow_DecisionSegment',
        workflow_decision_tags_attribute:
          'CopyTemplateDialog_Warning_Workflow_DecisionAttribute',
        workflow_decision_message:
          'CopyTemplateDialog_Warning_Workflow_DecisionMessage',
        workflow_multidecision_segment:
          'CopyTemplateDialog_Warning_Workflow_MultiDecisionSegment',
        workflow_multidecision_tags_attribute:
          'CopyTemplateDialog_Warning_Workflow_MultiDecisionAttribute',
        workflow_multidecision_message:
          'CopyTemplateDialog_Warning_Workflow_MultiDecisionMessage',
        workflow_promotion: 'CopyTemplateDialog_Warning_Workflow_Promotion',
        workflow_external_audience:
          'CopyTemplateDialog_Warning_Workflow_ExternalAudience',
        workflow_microsite: 'CopyTemplateDialog_Warning_Workflow_Microsite',
        workflow_snippets: 'CopyTemplateDialog_Warning_Workflow_Snippets',
        workflow_user_attribute:
          'CopyTemplateDialog_Warning_Workflow_UserAttribute',
        email_from_address: 'CopyTemplateDialog_Warning_EmailFromAddress',
        sms_from_address: 'CopyTemplateDialog_Warning_SmsFromName',
        bee_builder_images: 'CopyTemplateDialog_Warning_BeeBuilderImages',
        mxa_from_names_warning: 'CopyTemplateDialog_Warning_MxaFromNames',
        synced_rows: 'CopyTemplateDialog_Warning_SyncedRows'
      }
    }
  },
  computed: {
    filteredProjects() {
      return this.availableProjects.filter(project =>
        this.projectFilter
          .toLowerCase()
          .split(' ')
          .map(searchTerm =>
            project.name
              .concat(
                ' ',
                this.crossProvider ? project.company.name : '',
                ' ',
                this.crossProvider ? project.provider.name : ''
              )
              .toLowerCase()
              .includes(searchTerm)
          )
          .reduce((thisValue, total) => thisValue && total)
      )
    },
    projectId() {
      return this.$route.query.project_id
    },
    crossProvider() {
      return this.$can('CampaignTemplate:CrossProviderAccess')
    }
  },
  methods: {
    onCopyBtnClick() {
      this.copyTemplate()
    },
    copyTemplate() {
      this.polling = true
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `campaigns/${this.templateId}/copy-to-project`,
          data: { destination_project_id: this.selectedProject.id }
        })
      )
        .then(({ data }) => {
          this.taskId = data.task_id
          this.copyTemplatePoll()
        })
        .catch(error => {
          this.polling = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },
    copyTemplatePoll() {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `task/records/${this.taskId}`
        })
      )
        .then(({ data }) => {
          switch (data.status) {
            case 0: // TASK_STATUS_NEW
            case 1: // TASK_STATUS_IN_PROGRESS
              setTimeout(() => {
                this.copyTemplatePoll()
              }, 500)
              break
            case 2: // TASK_STATUS_DONE
              this.polling = false
              this.handleClose()
              this.openCopiedTemplate(data.result)
              this.$message({
                message: this.$t('CopyTemplateDialog_CopiedSuccessfully'),
                type: 'success'
              })
              break
            default:
              this.polling = false
              this.$message({
                message: data.errors || this.$t('CopyTemplateDialog_Error'),
                type: 'error'
              })
              break
          }
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },
    onNextBtnClick() {
      this.errors = []
      this.warnings = []
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `campaigns/${this.templateId}/validate-copy`,
          data: { destination_project_id: this.selectedProject.id }
        })
      )
        .then(({ data }) => {
          this.showStepTwo = true
          this.errors = data.errors
          this.warnings = data.warnings
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },
    onBackBtnClick() {
      if (this.showStepTwo) {
        this.showStepTwo = false
      }
    },
    handleClose() {
      this.$emit('update:dialogVisible', false)
      this.$parent.$data.key = new Date().getTime()
      this.showStepOne = this.showStepTwo = this.showStepTriggers = false
    },
    dateFormatter(row) {
      if (!row.create_time) {
        return '-'
      }
      return moment.unix(row.create_time).format('YYYY-MM-DD HH:mm')
    },
    openCopiedTemplate(data) {
      window.open(
        `/push/campaign/template/update?id=${data.copied_campaign_id}&project_id=${data.destination_project_id}`,
        '_blank'
      )
    }
  },
  watch: {
    dialogVisible() {
      if (this.dialogVisible) {
        this.showStepOne = true
      }
    },
    campaignType() {
      this.triggerType = null
    }
  }
}
</script>

<style scoped lang="scss">
/deep/ .el-dialog__header {
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 20px;
}

/deep/ .el-dialog__body {
  padding-bottom: 10px;
}

.sub-title {
  color: #606266;
  font-size: 14px;
  padding-top: 5px;
}

.table-container.outlined {
  border: 1px solid #dbdee7;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}
.dialog-body {
  height: 400px;
  overflow: scroll;
  word-break: initial;

  .warning-msg {
    font-weight: 600;
    font-size: 14px;
    &.centered {
      text-align: center;
    }
  }
  .warning-items {
    padding-top: 16px;
    ul {
      list-style: disc;
    }
    li {
      margin-left: 1em;
    }
  }
}

/deep/ .el-dialog__footer {
  border-top: 1px solid #ebeef5;
  padding: 20px;
}

.dialog-footer {
  display: flex;
  justify-content: space-between;
  flex-direction: row-reverse;
}

>>> .el-dialog__wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

>>> .el-dialog {
  max-width: 90%;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaigns/components/CopyTemplateDialog.vue